import React from 'react';
import i18n from '../../i18n';
import { AppDataContext } from '../../context/Context';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import { TextField, CircularProgress, Typography } from '@mui/material';
import debounce from 'lodash/debounce';
import { getExtractValue } from '../../utils/formatName';
import { fetchSearchItems } from './helper/fetchSearchItems';

export default class DialogServerSearchField extends React.Component {
    static contextType = AppDataContext;

    constructor(props) {
        super(props);
        this.state = {
            options: props.options || [],
            inputValue: '',
            loading: false
        };
        this.debouncedFetchOptions = debounce(this.fetchOptions, 600);
    }

    componentDidMount() {
        this.state.options.length === 0 && this.fetchOptions('');
    }

    componentWillUnmount() {
        this.debouncedFetchOptions.cancel();
    }

    fetchOptions = async (query) => {
        // if (!query && this.state.options.length > 0) {
        //     return;
        // }

        this.setState({ loading: true });
        const server =
            this.context.getState().server[this.props.field.searchController];
        const searchFields = this.props.field.searchFields;
        // prefilter narrows what may be picked at all, whatever is typed; limit and
        // sort decide how many show and which first (the API's own order otherwise).
        const { outputField = 'id', limit, prefilter = [], sort = [] } = this.props.field;
        try {
            const res = await fetchSearchItems(searchFields, query, server, limit, prefilter, sort);
            if (res.error !== 'OK') {
                throw new Error('Error fetching options');
            }
            const data = res.data;
            const { show, translate, nestedObject, secondaryObject, optionDetails } =
                this.props.field;
            const mappedItems = data.map((item) => {
                const val = getExtractValue(
                    item,
                    show,
                    nestedObject,
                    secondaryObject
                );
                const translatedValue = translate
                    ? i18n.t(`${translate}.${val}`)
                    : val;
                return {
                    [outputField]: item[outputField],
                    value: translatedValue,
                    ...(optionDetails ? { details: optionDetails(item) } : {})
                };
            });
            this.setState({ options: mappedItems });
        } catch (error) {
            console.error('Error fetching options:', error);
            this.setState({ options: [] });
        } finally {
            this.setState({ loading: false });
        }
    };

    checkIfValueExists = (value) => {
        const { options } = this.state;
        const { outputField = 'id' } = this.props.field;
        return options.some(
            (item) => item?.[outputField]?.toLowerCase?.() === value.toLowerCase()
        );
    };

    handleInputChange = (event, newInputValue) => {
        if (this.checkIfValueExists(newInputValue)) {
            this.setState({ inputValue: newInputValue });
            return;
        }
        this.setState({ inputValue: newInputValue });
        this.debouncedFetchOptions(newInputValue);
    };

    handleChanged = (field, value) => {
        const { outputField = 'id' } = this.props.field;
        if (value === null) {
            this.props.onChange(field, null);
        } else {
            this.props.onChange(field, value[outputField]);
        }
    };

    // A field with optionDetails(item) shows them above the value, so that rows whose
    // value alone says little (a uuid) can be told apart; the value stays what the
    // input shows once picked.
    renderDetailedOption = ({ key, ...props }, option) => (
        <li key={key} {...props}>
            <div style={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap>{option.details}</Typography>
                <Typography variant="caption" color="text.secondary" component="div" noWrap>
                    {option.value}
                </Typography>
            </div>
        </li>
    );

    render() {
        const { field, value } = this.props;
        const { options, loading } = this.state;
        const { outputField = 'id' } = field;

        const translatedName = i18n.t(`columns.${field.label}`);
        return (
            <FormControl fullWidth>
                <Autocomplete
                    value={
                        options.find((item) => item[outputField] === value) ||
                        null
                    }
                    onChange={(event, newValue) => {
                        this.handleChanged(field.id, newValue);
                    }}
                    onInputChange={this.handleInputChange}
                    options={options}
                    loading={loading}
                    getOptionLabel={(option) => option.value || ''}
                    // The server already searched every searchField for what was typed;
                    // filtering its answer again by the shown value hid a row found by
                    // another field (a payin's merchant id, found but not its uuid).
                    filterOptions={(found) => found}
                    renderOption={field.optionDetails ? this.renderDetailedOption : undefined}
                    isOptionEqualToValue={(option, val) =>
                        option[outputField] === val[outputField]
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={translatedName}
                            variant="outlined"
                            required={field.required}
                            helperText={
                                field.helperText ? i18n.t(`helperTexts.${field.helperText}`) : ''
                            }
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? (
                                            <CircularProgress
                                                color="inherit"
                                                size={20}
                                            />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }}
                        />
                    )}
                />
            </FormControl>
        );
    }
}
